import type { FieldValues } from 'react-hook-form'
import { apiErrorMessage } from '@/shared/apiError'
import type { EntityFormConfig } from '@/shared/entities/types'
import { nextCode } from '@/services/sequenceApi'

/** Placeholder for an empty code field on create (auto-number). */
export const AUTO_CODE_PLACEHOLDER = 'Automático'

const AUTO_CODE_WRAPPED = Symbol.for('syscolect.withAutoCode')

export type WithAutoCodeOpts = {
  entity: string
  field: string
  orgId: string | undefined
  usePoint: string
}

/** True when `config` was already produced by {@link withAutoCode}. */
export function isAutoCodeWrapped(config: object): boolean {
  return (config as { [AUTO_CODE_WRAPPED]?: boolean })[AUTO_CODE_WRAPPED] === true
}

/**
 * Wraps an {@link EntityFormConfig} so create-with-empty-code allocates via
 * `/sequence/v1/next` on save, and skips the required-code validation until then.
 *
 * Injects the allocated value into `values[field]` before delegating to the
 * original `onSave` (so `toDomain` / `toWriteInput` see a filled code).
 */
export function withAutoCode<TRow extends { id: string }, TForm extends FieldValues>(
  config: EntityFormConfig<TRow, TForm>,
  opts: WithAutoCodeOpts,
): EntityFormConfig<TRow, TForm> {
  if (isAutoCodeWrapped(config)) return config

  const { entity, field, orgId, usePoint } = opts

  const wrapped: EntityFormConfig<TRow, TForm> = {
    ...config,
    onValidate(values) {
      const errors = config.onValidate?.(values)
      const record = values as Record<string, unknown>
      if (!values.id && record[field] == null && errors?.[field]) {
        const { [field]: _removed, ...rest } = errors
        return Object.keys(rest).length > 0 ? rest : undefined
      }
      return errors
    },
    async onSave(values, isNew, opts) {
      let nextValues = values
      if (isNew && (values as Record<string, unknown>)[field] == null) {
        if (!orgId) throw new Error('Organização não selecionada.')
        try {
          const code = await nextCode(orgId, { entity, field, usePoint })
          nextValues = { ...values, [field]: code }
        } catch (error) {
          const detail = apiErrorMessage(error, {
            fallback: 'Tente novamente ou informe o código manualmente.',
          })
          throw new Error(`A numeração automática falhou. ${detail}`)
        }
      }
      return config.onSave(nextValues, isNew, opts)
    },
  }

  Object.defineProperty(wrapped, AUTO_CODE_WRAPPED, { value: true })
  return wrapped
}
